import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import {
  FontAwesomeIcon,
} from "@fortawesome/react-fontawesome";

import {
  faArrowRotateRight,
  faClock,
  faEye,
  faSearch,
  faUserCheck,
  faUserClock,
  faUserTie,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

import {
  toast,
} from "react-hot-toast";

import {
  ApiError,
} from "../api/apiError";

import {
  accountUpgradeRequestsApi,
} from "../api/accountUpgradeRequestsApi";

import type {
  AccountUpgradeRequest,
} from "../types/account-upgrade-requests.types";

import "../screenstyles/AccountUpgradeRequests.css";

function formatTimestamp(
  value: any,
): string {
  if (!value) {
    return "—";
  }

  let date:
    Date |
    null = null;

  if (
    typeof value ===
    "string"
  ) {
    date =
      new Date(value);
  } else {
    const seconds =
      value.seconds ??
      value._seconds;

    if (
      typeof seconds ===
      "number"
    ) {
      date =
        new Date(
          seconds *
            1000,
        );
    }
  }

  if (
    !date ||
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return "—";
  }

  return new Intl.DateTimeFormat(
    "ar-IQ",
    {
      year:
        "numeric",

      month:
        "short",

      day:
        "2-digit",

      hour:
        "2-digit",

      minute:
        "2-digit",
    },
  ).format(date);
}

function getStatusLabel(
  status:
    string |
    null |
    undefined,
) {
  switch (
    status
  ) {
    case "pending":
      return "بانتظار الموافقة";

    case "approved":
      return "تمت الموافقة";

    case "rejected":
      return "مرفوض";

    case "cancelled":
      return "ملغي";

    default:
      return status ||
        "غير محدد";
  }
}

function getRequestedType(
  request:
    AccountUpgradeRequest,
) {
  return (
    request
      .requestedAccountType ||
    "غير محدد"
  );
}

const AccountUpgradeRequests:
React.FC = () => {
  const navigate =
    useNavigate();

  const [
    requests,
    setRequests,
  ] =
    useState<
      AccountUpgradeRequest[]
    >([]);

  const [
    loading,
    setLoading,
  ] =
    useState(true);

  const [
    search,
    setSearch,
  ] =
    useState("");

  const [
    statusFilter,
    setStatusFilter,
  ] =
    useState("");

  const [
    typeFilter,
    setTypeFilter,
  ] =
    useState("");

  const [
    totalMatched,
    setTotalMatched,
  ] =
    useState(0);

  const fetchRequests =
    useCallback(
      async () => {
        try {
          setLoading(
            true,
          );

          const result =
            await accountUpgradeRequestsApi
              .list({
                status:
                  statusFilter ||
                  undefined,

                requestedAccountType:
                  typeFilter ||
                  undefined,

                limit: 50,
              });

          setRequests(
            result.items ??
              [],
          );

          setTotalMatched(
            result.totalMatched ??
              result.count ??
              0,
          );
        } catch (
          error
        ) {
          console.error(
            "Error loading account upgrade requests:",
            error,
          );

          if (
            error instanceof
            ApiError
          ) {
            if (
              error.status ===
              403
            ) {
              toast.error(
                "لا تمتلك صلاحية عرض طلبات ترقية الحساب",
              );

              return;
            }

            toast.error(
              error.message ||
                "حدث خطأ أثناء تحميل الطلبات",
            );

            return;
          }

          toast.error(
            "تعذر الاتصال بالخادم",
          );
        } finally {
          setLoading(
            false,
          );
        }
      },
      [
        statusFilter,
        typeFilter,
      ],
    );

  useEffect(() => {
    fetchRequests();
  }, [
    fetchRequests,
  ]);

  const filteredRequests =
    useMemo(
      () => {
        const term =
          search
            .trim()
            .toLowerCase();

        if (!term) {
          return requests;
        }

        return requests.filter(
          request => {
            const values = [
              request.fullName,
              request.phoneNumber,
              request.emailAddress,
              request.email,
              request.companyName,
              request.requestId,
              request.userId,
            ];

            return values.some(
              value =>
                typeof value ===
                  "string" &&
                value
                  .toLowerCase()
                  .includes(
                    term,
                  ),
            );
          },
        );
      },
      [
        requests,
        search,
      ],
    );

  const stats =
    useMemo(
      () => ({
        total:
          totalMatched ||
          requests.length,

        pending:
          requests.filter(
            request =>
              request.status ===
              "pending",
          ).length,

        approved:
          requests.filter(
            request =>
              request.status ===
              "approved",
          ).length,

        rejected:
          requests.filter(
            request =>
              request.status ===
              "rejected",
          ).length,
      }),
      [
        requests,
        totalMatched,
      ],
    );

  return (
    <div className="account-upgrade-page">

      <section className="account-upgrade-header">
        <div>
          <span className="account-upgrade-eyebrow">
            إدارة الحسابات
          </span>

          <h1>
            طلبات ترقية الحساب
          </h1>

          <p>
            مراجعة طلبات التحويل
            إلى وكيل أو منصب
            والموافقة عليها أو
            رفضها.
          </p>
        </div>

        <button
          type="button"
          className="account-upgrade-refresh-btn"
          disabled={
            loading
          }
          onClick={
            fetchRequests
          }
        >
          <FontAwesomeIcon
            icon={
              faArrowRotateRight
            }
          />

          تحديث
        </button>
      </section>

      <section className="account-upgrade-stats">
        <div>
          <FontAwesomeIcon
            icon={
              faUserTie
            }
          />

          <span>
            إجمالي الطلبات
          </span>

          <strong>
            {stats.total}
          </strong>
        </div>

        <div>
          <FontAwesomeIcon
            icon={
              faUserClock
            }
          />

          <span>
            بانتظار الموافقة
          </span>

          <strong>
            {stats.pending}
          </strong>
        </div>

        <div>
          <FontAwesomeIcon
            icon={
              faUserCheck
            }
          />

          <span>
            تمت الموافقة
          </span>

          <strong>
            {stats.approved}
          </strong>
        </div>

        <div>
          <FontAwesomeIcon
            icon={faXmark}
          />

          <span>
            مرفوضة
          </span>

          <strong>
            {stats.rejected}
          </strong>
        </div>
      </section>

      <section className="account-upgrade-toolbar">
        <div className="account-upgrade-search">
          <FontAwesomeIcon
            icon={faSearch}
          />

          <input
            type="text"
            placeholder="بحث بالاسم أو الهاتف أو البريد..."
            value={search}
            onChange={
              event =>
                setSearch(
                  event
                    .target
                    .value,
                )
            }
          />
        </div>

        <select
          value={
            statusFilter
          }
          onChange={
            event =>
              setStatusFilter(
                event
                  .target
                  .value,
              )
          }
        >
          <option value="">
            جميع الحالات
          </option>

          <option value="pending">
            بانتظار الموافقة
          </option>

          <option value="approved">
            تمت الموافقة
          </option>

          <option value="rejected">
            مرفوضة
          </option>

          <option value="cancelled">
            ملغاة
          </option>
        </select>

        <select
          value={
            typeFilter
          }
          onChange={
            event =>
              setTypeFilter(
                event
                  .target
                  .value,
              )
          }
        >
          <option value="">
            جميع أنواع الطلبات
          </option>

          <option value="منصب">
            منصب
          </option>

          <option value="وكيل">
            وكيل
          </option>
        </select>
      </section>

      <section className="account-upgrade-table-card">
        {loading ? (
          <div className="account-upgrade-state">
            جاري تحميل طلبات
            الترقية...
          </div>
        ) : filteredRequests.length ===
          0 ? (
          <div className="account-upgrade-state">
            لا توجد طلبات مطابقة.
          </div>
        ) : (
          <div className="account-upgrade-table-wrapper">
            <table className="account-upgrade-table">
              <thead>
                <tr>
                  <th>
                    #
                  </th>

                  <th>
                    المستخدم
                  </th>

                  <th>
                    الهاتف
                  </th>

                  <th>
                    الحساب المطلوب
                  </th>

                  <th>
                    الشركة
                  </th>

                  <th>
                    الحالة
                  </th>

                  <th>
                    التاريخ
                  </th>

                  <th>
                    الإجراء
                  </th>
                </tr>
              </thead>

              <tbody>
                {filteredRequests.map(
                  (
                    request,
                    index,
                  ) => (
                    <tr
                      key={
                        request.requestId
                      }
                    >
                      <td>
                        {index +
                          1}
                      </td>

                      <td>
                        <div className="account-upgrade-user-cell">
                          <strong>
                            {request.fullName ||
                              "مستخدم بدون اسم"}
                          </strong>

                          <span>
                            {request.emailAddress ||
                              request.email ||
                              request.userId}
                          </span>
                        </div>
                      </td>

                      <td dir="ltr">
                        {request.phoneNumber ||
                          "—"}
                      </td>

                      <td>
                        <span
                          className={`account-upgrade-type account-upgrade-type-${
                            getRequestedType(
                              request,
                            ) ===
                            "وكيل"
                              ? "agent"
                              : "technician"
                          }`}
                        >
                          {getRequestedType(
                            request,
                          )}
                        </span>
                      </td>

                      <td>
                        {request.companyName ||
                          "—"}
                      </td>

                      <td>
                        <span
                          className={`account-upgrade-status status-${request.status}`}
                        >
                          {getStatusLabel(
                            request.status,
                          )}
                        </span>
                      </td>

                      <td>
                        <div className="account-upgrade-date">
                          <FontAwesomeIcon
                            icon={
                              faClock
                            }
                          />

                          {formatTimestamp(
                            request.createdAt,
                          )}
                        </div>
                      </td>

                      <td>
                        <button
                          type="button"
                          className="account-upgrade-view-btn"
                          onClick={() =>
                            navigate(
                              `/account-upgrade-requests/${request.requestId}`,
                            )
                          }
                        >
                          <FontAwesomeIcon
                            icon={
                              faEye
                            }
                          />

                          عرض
                        </button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

export default AccountUpgradeRequests;